import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button, Input, Label } from "@asm/ui";
import { UserPlus, AlertTriangle, ArrowLeft, Mail, Lock, User, KeyRound } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useAuthStore } from "@/lib/auth";

export default function Signup() {
  const [step, setStep] = useState<"signup" | "confirm">("signup");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const { t } = useTranslation("auth");
  const { signup, confirmSignup, isLoading, error, clearError } = useAuthStore();
  const navigate = useNavigate();

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await signup(email, password, name);
      setStep("confirm");
    } catch {
      // Error is handled by the store
    }
  };

  const handleConfirm = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    try {
      await confirmSignup(email, code);
      navigate("/login");
    } catch {
      // Error is handled by the store
    }
  };

  if (step === "confirm") {
    return (
      <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-8 w-full max-w-md">
        <div className="flex flex-col items-center mb-6">
          <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20 mb-4">
            <KeyRound className="h-8 w-8 text-cyan-400" />
          </div>
          <h1 className="text-2xl font-bold">{t("verify.title")}</h1>
          <p className="text-muted-foreground text-sm mt-1 text-center">
            {t("verify.description", { email })}
          </p>
        </div>
        <form onSubmit={handleConfirm} className="space-y-4">
          {error && (
            <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
              <AlertTriangle className="h-4 w-4 flex-shrink-0" />
              {error}
            </div>
          )}
          <div className="space-y-2">
            <Label htmlFor="code" className="text-muted-foreground">{t("verify.code")}</Label>
            <Input
              id="code"
              type="text"
              inputMode="numeric"
              pattern="[0-9]*"
              maxLength={6}
              placeholder={t("verify.codePlaceholder")}
              value={code}
              onChange={(e) => setCode(e.target.value)}
              required
              autoComplete="one-time-code"
              autoFocus
              className="bg-muted/50 border-border text-center text-2xl font-mono tracking-widest"
            />
          </div>
          <Button
            type="submit"
            className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold"
            disabled={isLoading}
          >
            {isLoading ? t("verify.submitting") : t("verify.submit")}
          </Button>
          <Button type="button" variant="ghost" className="w-full" onClick={() => setStep("signup")}>
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t("verify.back")}
          </Button>
        </form>
      </div>
    );
  }

  return (
    <div className="animate-fade-in-up rounded-lg border border-border bg-card/50 p-8 w-full max-w-md">
      <div className="flex flex-col items-center mb-6">
        <div className="p-3 rounded-lg bg-cyan-500/10 border border-cyan-500/20 mb-4">
          <UserPlus className="h-8 w-8 text-cyan-400" />
        </div>
        <h1 className="text-2xl font-bold">{t("signup.title")}</h1>
        <p className="text-muted-foreground text-sm mt-1">
          {t("signup.description")}
        </p>
      </div>
      <form onSubmit={handleSignup} className="space-y-4">
        {error && (
          <div className="p-3 text-sm text-red-400 bg-red-500/10 border border-red-500/20 rounded-lg flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 flex-shrink-0" />
            {error}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="name" className="text-muted-foreground">
            {t("signup.name")}
          </Label>
          <div className="relative">
            <User className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="name"
              type="text"
              placeholder={t("signup.namePlaceholder")}
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
              className="bg-muted/50 border-border pl-10"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-muted-foreground">
            {t("signup.email")}
          </Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              placeholder={t("signup.emailPlaceholder")}
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              autoComplete="email"
              className="bg-muted/50 border-border pl-10"
            />
          </div>
        </div>
        <div className="space-y-2">
          <Label htmlFor="password" className="text-muted-foreground">
            {t("signup.password")}
          </Label>
          <div className="relative">
            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              autoComplete="new-password"
              className="bg-muted/50 border-border pl-10"
            />
          </div>
          <p className="text-xs text-muted-foreground">{t("signup.passwordHint")}</p>
        </div>
        <Button
          type="submit"
          className="w-full bg-cyan-500 hover:bg-cyan-400 text-black font-semibold glow-cyan"
          disabled={isLoading}
        >
          {isLoading ? t("signup.submitting") : t("signup.submit")}
        </Button>
        <p className="text-sm text-center text-muted-foreground">
          {t("signup.hasAccount")}{" "}
          <Link to="/login" className="text-cyan-400 hover:text-cyan-300 hover:underline">
            {t("signup.signIn")}
          </Link>
        </p>
      </form>
    </div>
  );
}
